import {
  Box,
  FormSelect,
  FormToggle,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@husqvarna/ui-core";
import { FormSelectOption } from "@husqvarna/ui-core/dist/components/form-select";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { debounce } from "lodash";

import PageContiner from "../../shared/components/PageContainer/PageContainer";
import { RootState } from "../../shared/store";
import { FuelType, AmountUnit, UnitSystem } from "../models/Enums";
import UpdateCurrentSetupModel from "../models/UpdateCurrentSetupModel";
import Project from "../models/Project";
import { useCreateCurrentSetupMutation } from "../projectApi";
import { ComparisonProduct } from "../models/ComparisonProduct";
import ServiceYearCosts from "../components/Services/ServiceCostPerYear";
import FormTextWithSuffix from "../../shared/components/FormTextWithSuffix";
import Toggle from "../../shared/components/Toggle/Toggle";

const createDefaultValues = (project?: Project) => {
  const cp = project?.proposal.comparisonProduct;
  const compProduct: ComparisonProduct = {
    investmentCost: cp ? cp.investmentCost : undefined,
    quantity: cp ? cp.quantity : 1,
    name: "",
    fuelConsumptionPerCut: cp ? cp.fuelConsumptionPerCut : undefined,
    fuelType: cp ? cp.fuelType : FuelType.Diesel,
    fuelPricePerUnit: cp ? cp.fuelPricePerUnit : undefined,
    serviceCostPerYear: cp ? cp.serviceCostPerYear : [],
    depreciationYears: cp ? cp.depreciationYears : 1,
    restValuePercentAfterDepreciation: cp
      ? cp.restValuePercentAfterDepreciation
      : undefined,
    maintenanceCostPerCut: cp ? cp.maintenanceCostPerCut : undefined,
    averageNoOfCutsPerWeek: cp ? cp.averageNoOfCutsPerWeek : 0.5,
    labourHoursNeededPerCut: cp ? cp.labourHoursNeededPerCut : undefined,
    serviceCostUnit: cp ? cp.serviceCostUnit : AmountUnit.Currency,
  };

  const formModel: UpdateCurrentSetupModel = {
    projectId: project ? project.id : "",
    isCuttingOutsourced: project ? project.cuttingIsOutsourced : true,
    outsourcingCostPerYear: project ? project.outsourcingCostPerYear : 0,
    comparisonProduct: compProduct,
    useCurrentSetup: project?.useCurrentSetup ?? true,
  };

  return formModel;
};

const CurrentSetup = () => {
  const intl = useIntl();
  const project = useSelector(
    (state: RootState) => state.project.selectedProject,
  );
  const [useCurrentSetup, setUseCurrentSetup] = useState<boolean>(
    project ? project.useCurrentSetup : true,
  );
  const [outsourced, setOutsourced] = useState(
    project ? project.cuttingIsOutsourced : true,
  );
  const {
    register,
    setValue,
    reset,
    formState: { errors, isDirty, isValid },
    watch,
  } = useForm<UpdateCurrentSetupModel>({
    defaultValues: useMemo(() => {
      return createDefaultValues(project);
    }, [project]),
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const [createCurrentSetup] = useCreateCurrentSetupMutation();
  const isSavingRef = useRef(false);
  const isValidRef = useRef(isValid);
  isValidRef.current = isValid;

  const saveFunction = useCallback(
    (model: UpdateCurrentSetupModel) => {
      if (isSavingRef.current || !isValidRef.current) return;
      isSavingRef.current = true;

      // Clean the model before sending
      const cleanModel: UpdateCurrentSetupModel = {
        projectId: model.projectId,
        isCuttingOutsourced: model.isCuttingOutsourced,
        outsourcingCostPerYear: model.outsourcingCostPerYear ?? 0,
        useCurrentSetup: model.useCurrentSetup,
        comparisonProduct: {
          investmentCost: model.comparisonProduct?.investmentCost,
          quantity: model.comparisonProduct?.quantity,
          name: model.comparisonProduct?.name || "",
          fuelConsumptionPerCut: model.comparisonProduct?.fuelConsumptionPerCut,
          fuelType: model.comparisonProduct?.fuelType,
          fuelPricePerUnit: model.comparisonProduct?.fuelPricePerUnit,
          serviceCostPerYear: model.comparisonProduct?.serviceCostPerYear,
          depreciationYears: model.comparisonProduct?.depreciationYears,
          restValuePercentAfterDepreciation:
            model.comparisonProduct?.restValuePercentAfterDepreciation,
          maintenanceCostPerCut: model.comparisonProduct?.maintenanceCostPerCut,
          averageNoOfCutsPerWeek:
            model.comparisonProduct?.averageNoOfCutsPerWeek,
          labourHoursNeededPerCut:
            model.comparisonProduct?.labourHoursNeededPerCut,
          serviceCostUnit: model.comparisonProduct?.serviceCostUnit,
        },
      };

      createCurrentSetup(cleanModel)
        .unwrap()
        .then(() => {})
        .catch((err) => {
          console.error("Save failed:", err);
        })
        .finally(() => {
          isSavingRef.current = false;
        });
    },
    [createCurrentSetup],
  );

  const debouncedSave = useMemo(
    () => debounce(saveFunction, 750),
    [saveFunction],
  );

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (type === "change" && !isSavingRef.current) {
        debouncedSave(value as UpdateCurrentSetupModel);
      }
    });
    return () => {
      subscription.unsubscribe();
      debouncedSave.cancel();
    };
  }, [watch, debouncedSave]);

  const submitCurrentSetup = (toggelValue: boolean) => {
    setValue("useCurrentSetup", toggelValue, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setUseCurrentSetup(toggelValue);
    const currentValues = watch();
    saveFunction(currentValues as UpdateCurrentSetupModel);
  };

  useEffect(() => {
    if (!project?.id || isDirty) return;
    reset(createDefaultValues(project));
    setOutsourced(project.cuttingIsOutsourced);
    setUseCurrentSetup(project.useCurrentSetup);
  }, [project?.id, reset]);

  const quantityOptions: FormSelectOption[] = [];
  const cutsPerWeekOptions: FormSelectOption[] = [{ label: "0.5", value: 0.5 }];
  const depreciationOptions: FormSelectOption[] = [];
  const fuelTypeOptions: FormSelectOption[] = [
    {
      value: FuelType.Diesel,
      label: intl.formatMessage({
        id: "project.fuelType.0",
        defaultMessage: "Diesel",
      }),
    },
    {
      value: FuelType.Petrol,
      label: intl.formatMessage({
        id: "project.fuelType.1",
        defaultMessage: "Petrol",
      }),
    },
  ];

  for (let i = 1; i <= 20; i++) {
    quantityOptions.push({ label: i.toString(), value: i });
    cutsPerWeekOptions.push({ label: i.toString(), value: i });
    if (i <= 8) depreciationOptions.push({ label: i.toString(), value: i });
  }


  function getPricePerUnitLabel(): string {
    const unitSystem = project?.region.unitSystem;
    switch (unitSystem) {
      case UnitSystem.Imperial:
        return intl.formatMessage({
          id: "project.currentSetup.cuttingNotOutsourced.pricePerGallon",
          defaultMessage: "Price per gallon",
        });

      default:
        return intl.formatMessage({
          id: "project.currentSetup.cuttingNotOutsourced.pricePerLitre",
          defaultMessage: "Price per litre",
        });
    }
  }

  // Helper to extract value from event or direct value
  const extractValue = (valueOrEvent: any) => {
    if (typeof valueOrEvent === "object" && valueOrEvent?.target) {
      const raw = valueOrEvent.target.value;
      // If it's a number string, convert to number, else keep as string
      return !isNaN(Number(raw)) && raw !== "" ? Number(raw) : raw;
    }
    return valueOrEvent;
  };

  // Update the FormSelect onChangeListener
  const handleFormSelectChange = (
    field:
      | keyof UpdateCurrentSetupModel
      | `comparisonProduct.${keyof UpdateCurrentSetupModel["comparisonProduct"]}`,
    value: any,
  ) => {
    setValue(field, value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

    // Create a clean model from the form values
    const formValues = watch();
    const cleanModel: UpdateCurrentSetupModel = {
      projectId: formValues.projectId,
      isCuttingOutsourced: formValues.isCuttingOutsourced,
      outsourcingCostPerYear: formValues.outsourcingCostPerYear ?? 0,
      useCurrentSetup: formValues.useCurrentSetup,
      comparisonProduct: {
        investmentCost: formValues.comparisonProduct?.investmentCost,
        quantity: formValues.comparisonProduct?.quantity,
        name: formValues.comparisonProduct?.name || "",
        fuelConsumptionPerCut:
          formValues.comparisonProduct?.fuelConsumptionPerCut,
        fuelType: formValues.comparisonProduct?.fuelType,
        fuelPricePerUnit: formValues.comparisonProduct?.fuelPricePerUnit,
        serviceCostPerYear: formValues.comparisonProduct?.serviceCostPerYear,
        depreciationYears: formValues.comparisonProduct?.depreciationYears,
        restValuePercentAfterDepreciation:
          formValues.comparisonProduct?.restValuePercentAfterDepreciation,
        maintenanceCostPerCut:
          formValues.comparisonProduct?.maintenanceCostPerCut,
        averageNoOfCutsPerWeek:
          formValues.comparisonProduct?.averageNoOfCutsPerWeek,
        labourHoursNeededPerCut:
          formValues.comparisonProduct?.labourHoursNeededPerCut,
        serviceCostUnit: formValues.comparisonProduct?.serviceCostUnit,
      },
    };

    saveFunction(cleanModel);
  };

  return (
    <>
      <PageContiner
        headline={intl.formatMessage({
          id: "projects.currentSetup.heading",
          defaultMessage: "Current setup",
        })}
        headlineAction={
          <FormToggle
            checked={useCurrentSetup}
            onChange={(e: any) => submitCurrentSetup(e.checked)}
            label={intl.formatMessage({
              id: "project.currentSetup.useCurrentSetup",
              defaultMessage: "Use current setup",
            })}
          />
        }
        description={intl.formatMessage({
          id: "project.currentSetup.description",
          defaultMessage:
            "Use current setup to include comparison data in the exported PPT. If the customer doesn't have a current setup, or if it’s not relevant to include in the PPT, leave this section as it is.",
        })}
        noDivider={!useCurrentSetup}
      >
        <Box container flexDirection="column">
          {project && (
            <>
              {useCurrentSetup && (
                <form autoComplete="off">
                  <Box mb={6}>
                    <Toggle
                      options={[
                        {
                          label: intl.formatMessage({
                            id: "project.currentSetup.insourcedSetup",
                            defaultMessage: "Insourced setup",
                          }),
                          value: false,
                        },
                        {
                          label: intl.formatMessage({
                            id: "project.currentSetup.outsourcedSetup",
                            defaultMessage: "Outsourced setup",
                          }),
                          value: true,
                        },
                      ]}
                      value={outsourced}
                      onChange={(val) => {
                        setOutsourced(val);
                        handleFormSelectChange("isCuttingOutsourced", val);
                      }}
                    />
                  </Box>

                  <Typography mt={12} variant="Subtitle1">
                    <FormattedMessage
                      id="project.currentSetup.cuttingNotOutsourced.productHeading"
                      defaultMessage="Current mower"
                    />
                  </Typography>
                  {outsourced && (
                    <Box mt={6}>
                      <Box>
                        <Typography variant="Body2Medium" mb={2}>
                          <FormattedMessage
                            id="project.currentSetup.cuttingOutsourcedHeader"
                            defaultMessage="Outsourcing cost per year"
                          />
                        </Typography>
                        <Grid container spacing={6}>
                          <Grid item sm={6}>
                            <FormTextWithSuffix
                              id="machine-rest-value"
                              {...register("outsourcingCostPerYear", {
                                pattern: {
                                  value: /\d+/,
                                  message: intl.formatMessage({
                                    id: "general.formValidation.notNumeric",
                                    defaultMessage: "Needs to be a number",
                                    description:
                                      "Form error message for not a number",
                                  }),
                                },
                              })}
                              value={watch("outsourcingCostPerYear")}
                              suffix={project.region.currency}
                              error={errors.outsourcingCostPerYear?.message}
                              allowDecimals={false}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                  )}
                  {!outsourced && (
                    <Box mt={6}>
                      <Box>
                        <Grid container spacing={6}>
                          <Grid item sm={12} md={4} lg={4}>
                            <Typography
                              variant="Body2"
                              className="tcsp-medium"
                              mb={2}
                              text={intl.formatMessage({
                                id: "project.currentSetup.cuttingNotOutsourced.quantity",
                                defaultMessage: "Quantity",
                              })}
                            />
                            <FormSelect
                              options={quantityOptions}
                              label=""
                              selectedValue={watch(
                                "comparisonProduct.quantity",
                              )}
                              onChangeListener={(valueOrEvent: any) => {
                                const value = extractValue(valueOrEvent);
                                handleFormSelectChange(
                                  "comparisonProduct.quantity",
                                  value,
                                );
                              }}
                            />
                          </Grid>
                          <Grid item sm={12} md={4} lg={4}>
                            <Box flexDirection="column">
                              <Typography
                                variant="Body2"
                                tag="label"
                                className="tcsp-medium"
                                htmlFor="labourHours"
                                mb={2}
                                text={intl.formatMessage({
                                  id: "project.currentSetup.cuttingNotOutsourced.labourHours",
                                  defaultMessage: "Labour hours per cut",
                                })}
                              />
                              <FormTextWithSuffix
                                id="labourHours"
                                {...register(
                                  "comparisonProduct.labourHoursNeededPerCut",
                                  {
                                    pattern: {
                                      value: /^\d+(\.\d)?$/,
                                      message: intl.formatMessage({
                                        id: "general.formValidation.notOneDecimal",
                                        defaultMessage:
                                          "Needs to be a number with max 1 decimal",
                                        description:
                                          "Form error message for not a number",
                                      }),
                                    },
                                  },
                                )}
                                value={watch(
                                  "comparisonProduct.labourHoursNeededPerCut",
                                )}
                                placeholder={intl.formatMessage({
                                  id: "general.amount",
                                  defaultMessage: "Amount",
                                })}
                                suffix={intl.formatMessage({
                                  id: "general.hourAbbreviation",
                                  defaultMessage: "h",
                                })}
                                error={
                                  errors.comparisonProduct
                                    ?.labourHoursNeededPerCut?.message
                                }
                                allowDecimals={true}
                              />
                            </Box>
                          </Grid>
                          <Grid item sm={12} md={4} lg={4}>
                            <Box flexDirection="row" alignItems="center">
                              <Typography
                                variant="Body2"
                                className="tcsp-medium"
                                mb={2}
                                text={intl.formatMessage({
                                  id: "project.currentSetup.cuttingNotOutsourced.cutsPerWeek",
                                  defaultMessage: "Cuts per week",
                                })}
                              />

                              <Tooltip
                                toolTipText={intl.formatMessage({
                                  id: "project.currentSetup.cutsPerWeekTooltip",
                                  defaultMessage:
                                    "Total amount of cuts per week for all current mowers.",
                                })}
                              >
                                <IconButton
                                  mt={-2}
                                  icon="info"
                                  size="Medium"
                                  disabled={outsourced || !useCurrentSetup}
                                />
                              </Tooltip>
                            </Box>
                            <FormSelect
                              options={cutsPerWeekOptions}
                              label=""
                              selectedValue={watch(
                                "comparisonProduct.averageNoOfCutsPerWeek",
                              )}
                              onChangeListener={(valueOrEvent: any) => {
                                const value = extractValue(valueOrEvent);
                                handleFormSelectChange(
                                  "comparisonProduct.averageNoOfCutsPerWeek",
                                  value,
                                );
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                      <Box mt={12}>
                        <Typography variant="Subtitle2">
                          <FormattedMessage
                            id="project.currentSetup.cuttingNotOutsourced.fuelHeading"
                            defaultMessage="Fuel information"
                          />
                        </Typography>
                        <Box mt={8}>
                          <Grid container spacing={6}>
                            <Grid item sm={12} md={4} lg={4}>
                              <Typography
                                variant="Body2"
                                className="tcsp-medium"
                                mb={2}
                                text={intl.formatMessage({
                                  id: "project.currentSetup.cuttingNotOutsourced.fuelType",
                                  defaultMessage: "Fuel type",
                                })}
                              />
                              <FormSelect
                                options={fuelTypeOptions}
                                label=""
                                selectedValue={watch(
                                  "comparisonProduct.fuelType",
                                )}
                                onChangeListener={(valueOrEvent: any) => {
                                  console.log("valueOrEvent", valueOrEvent);
                                  const value = extractValue(valueOrEvent);
                                  handleFormSelectChange(
                                    "comparisonProduct.fuelType",
                                    value,
                                  );
                                }}
                              />
                            </Grid>
                            <Grid item sm={12} md={4} lg={4}>
                              <Box flexDirection="column">
                                <Typography
                                  tag="label"
                                  variant="Body2"
                                  className="tcsp-medium"
                                  pb={2}
                                  htmlFor="pricePerUnit"
                                  text={getPricePerUnitLabel()}
                                />

                                <FormTextWithSuffix
                                  id="pricePerUnit"
                                  {...register(
                                    "comparisonProduct.fuelPricePerUnit",
                                    {
                                      pattern: {
                                        value: /^\d+\.?\d*$/,
                                        message: intl.formatMessage({
                                          id: "general.formValidation.notDecimal",
                                          defaultMessage:
                                            "Needs to be a number or decimal (with period as decimal sign).",
                                          description:
                                            "Form error message for not a decimal",
                                        }),
                                      },
                                    },
                                  )}
                                  value={watch(
                                    "comparisonProduct.fuelPricePerUnit",
                                  )}
                                  placeholder={intl.formatMessage({
                                    id: "general.amount",
                                    defaultMessage: "Amount",
                                  })}
                                  suffix={project.region.currency}
                                  error={
                                    errors.comparisonProduct?.fuelPricePerUnit
                                      ?.message
                                  }
                                  allowDecimals={true}
                                />
                              </Box>
                            </Grid>
                            <Grid item sm={12} md={4} lg={4}>
                              <Box flexDirection="column">
                                <Typography
                                  tag="label"
                                  variant="Body2"
                                  className="tcsp-medium"
                                  pb={2}
                                  htmlFor="fuelConsumption"
                                  text={intl.formatMessage({
                                    id: "project.currentSetup.cuttingNotOutsourced.consumptionPerCut",
                                    defaultMessage: "Consumption per cut",
                                  })}
                                />

                                <FormTextWithSuffix
                                  id="fuelConsumption"
                                  {...register(
                                    "comparisonProduct.fuelConsumptionPerCut",
                                    {
                                      pattern: {
                                        value: /^\d+\.?\d*$/,
                                        message: intl.formatMessage({
                                          id: "general.formValidation.notDecimal",
                                          defaultMessage:
                                            "Needs to be a number or decimal (with period as decimal sign).",
                                          description:
                                            "Form error message for not a decimal",
                                        }),
                                      },
                                    },
                                  )}
                                  value={watch(
                                    "comparisonProduct.fuelConsumptionPerCut",
                                  )}
                                  placeholder={intl.formatMessage({
                                    id: "general.amount",
                                    defaultMessage: "Amount",
                                  })}
                                  suffix={
                                    project.region.unitSystem ===
                                    UnitSystem.Imperial
                                      ? intl.formatMessage({
                                          id: "general.gallonAbbreviation",
                                          defaultMessage: "gal",
                                        })
                                      : intl.formatMessage({
                                          id: "general.literAbbreviation",
                                          defaultMessage: "L",
                                        })
                                  }
                                  error={
                                    errors.comparisonProduct
                                      ?.fuelConsumptionPerCut?.message
                                  }
                                  allowDecimals={true}
                                />
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </Box>
                      <Box mt={12}>
                        <Typography variant="Subtitle2">
                          <FormattedMessage
                            id="project.currentSetup.cuttingNotSourced.investmentsHeading"
                            defaultMessage="Costs"
                          />
                        </Typography>
                        <Box my={4} mb={12}>
                          <Grid container spacing={6}>
                            <Grid item sm={12} md={6}>
                              <Box flexDirection="column">
                                <Typography
                                  tag="label"
                                  variant="Body2"
                                  className="tcsp-medium"
                                  pb={2}
                                  htmlFor="investment"
                                  text={intl.formatMessage({
                                    id: "project.currentSetup.costPerMower",
                                    defaultMessage:
                                      "Investment/purchase cost per mower",
                                  })}
                                />
                                <FormTextWithSuffix
                                  id="investment"
                                  {...register(
                                    "comparisonProduct.investmentCost",
                                    {
                                      pattern: {
                                        value: /^\d+\.?\d*$/,
                                        message: intl.formatMessage({
                                          id: "general.formValidation.notDecimal",
                                          defaultMessage:
                                            "Needs to be a number or decimal (with period as decimal sign).",
                                          description:
                                            "Form error message for not a decimal",
                                        }),
                                      },
                                    },
                                  )}
                                  value={watch(
                                    "comparisonProduct.investmentCost",
                                  )}
                                  placeholder={intl.formatMessage({
                                    id: "general.amount",
                                    defaultMessage: "Amount",
                                  })}
                                  suffix={project.region.currency}
                                  error={
                                    errors.comparisonProduct?.investmentCost
                                      ?.message
                                  }
                                  allowDecimals={true}
                                />
                              </Box>
                            </Grid>
                            <Grid item sm={12} md={6}>
                              <Box flexDirection="column">
                                <Box flexDirection="row" alignItems="center">
                                  <Typography
                                    tag="label"
                                    variant="Body2"
                                    className="tcsp-medium"
                                    pb={2}
                                    htmlFor="maintenance"
                                    text={intl.formatMessage({
                                      id: "project.currentSetup.maintenanceCost",
                                      defaultMessage:
                                        "Maintenance cost per cut",
                                    })}
                                  />

                                  <Tooltip
                                    toolTipText={intl.formatMessage({
                                      id: "project.currentSetup.maintenanceCostTooltip",
                                      defaultMessage:
                                        "Transportation, cleaning etc.",
                                    })}
                                  >
                                    <IconButton
                                      mt={-2}
                                      icon="info"
                                      size="Medium"
                                      disabled={outsourced || !useCurrentSetup}
                                    />
                                  </Tooltip>
                                </Box>

                                <FormTextWithSuffix
                                  id="maintenance"
                                  {...register(
                                    "comparisonProduct.maintenanceCostPerCut",
                                    {
                                      pattern: {
                                        value: /^\d+(\.\d+)?$/,
                                        message: intl.formatMessage({
                                          id: "general.formValidation.notDecimal",
                                          defaultMessage:
                                            "Needs to be a number or decimal (with period as decimal sign).",
                                          description:
                                            "Form error message for not a decimal",
                                        }),
                                      },
                                    },
                                  )}
                                  value={watch(
                                    "comparisonProduct.maintenanceCostPerCut",
                                  )}
                                  placeholder={intl.formatMessage({
                                    id: "general.amount",
                                    defaultMessage: "Amount",
                                  })}
                                  suffix={project.region.currency}
                                  error={
                                    errors.comparisonProduct
                                      ?.maintenanceCostPerCut?.message
                                  }
                                  allowDecimals={true}
                                />
                              </Box>
                            </Grid>
                            <Grid item sm={12} md={6}>
                              <Box flexDirection="column">
                                <Typography
                                  tag="label"
                                  variant="Body2"
                                  className="tcsp-medium"
                                  mb={2}
                                  text={intl.formatMessage({
                                    id: "project.currentSetup.depreciation",
                                    defaultMessage: "Depreciation years",
                                  })}
                                />
                                <FormSelect
                                  options={depreciationOptions}
                                  label=""
                                  selectedValue={watch(
                                    "comparisonProduct.depreciationYears",
                                  )}
                                  onChangeListener={(valueOrEvent: any) => {
                                    const value = extractValue(valueOrEvent);
                                    handleFormSelectChange(
                                      "comparisonProduct.depreciationYears",
                                      value,
                                    );
                                  }}
                                />
                              </Box>
                            </Grid>
                            <Grid item sm={12} md={6}>
                              <Box flexDirection="column">
                                <Box
                                  flexDirection="row"
                                  alignItems="center"
                                  mb={2}
                                >
                                  <Typography
                                    tag="label"
                                    variant="Body2"
                                    className="tcsp-medium"
                                    htmlFor="restOfValue"
                                    text={intl.formatMessage({
                                      id: "project.currentSetup.restOfvalue",
                                      defaultMessage: "Rest of value (%)",
                                    })}
                                  />

                                  <Tooltip
                                    toolTipText={intl.formatMessage({
                                      id: "project.currentSetup.restOfValueTooltip",
                                      defaultMessage:
                                        "After depreciation period, based on % of total equipment investment cost",
                                    })}
                                  >
                                    <IconButton
                                      my={-1}
                                      icon="info"
                                      size="Medium"
                                      disabled={outsourced || !useCurrentSetup}
                                    />
                                  </Tooltip>
                                </Box>

                                <FormTextWithSuffix
                                  id="restOfValue"
                                  {...register(
                                    "comparisonProduct.restValuePercentAfterDepreciation",
                                    {
                                      validate: (value) => {
                                        if (value === null || value === undefined || String(value) === "") return true;
                                        const num = Number(value);
                                        if (isNaN(num)) return intl.formatMessage({
                                          id: "general.formValidation.notNumber",
                                          defaultMessage: "Needs to be a number",
                                        });
                                        if (num < 0 || num > 100) return intl.formatMessage({
                                          id: "general.formValidation.percentageRange",
                                          defaultMessage: "Needs to be between 0 and 100",
                                        });
                                        return true;
                                      },
                                    },
                                  )}
                                  value={watch(
                                    "comparisonProduct.restValuePercentAfterDepreciation",
                                  )}
                                  placeholder={intl.formatMessage({
                                    id: "general.amount",
                                    defaultMessage: "Amount",
                                  })}
                                  suffix={"%"}
                                  error={
                                    errors.comparisonProduct
                                      ?.restValuePercentAfterDepreciation
                                      ?.message
                                  }
                                  allowDecimals={false}
                                  max={100}
                                  min={0}
                                />
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box mt={12}>
                          <ServiceYearCosts
                            errors={errors}
                            register={register}
                            setValue={setValue}
                            currency={project?.region.currency ?? ""}
                            costsFieldPath="comparisonProduct.serviceCostPerYear"
                            unitFieldPath="comparisonProduct.serviceCostUnit"
                            disabled={outsourced || !useCurrentSetup}
                            onUnitChange={(unit) =>
                              handleFormSelectChange(
                                "comparisonProduct.serviceCostUnit",
                                unit,
                              )
                            }
                          />
                        </Box>
                      </Box>
                    </Box>
                  )}
                </form>
              )}
            </>
          )}
        </Box>
      </PageContiner>
    </>
  );
};
export default CurrentSetup;
